/**
 * 상품명에 들어간 규격(인치·cm 등)이 남긴 옵션에 없는 값이면 지운다.
 * 1688 원문 제목에는 다른 규격(예: "14寸15.6")이 적혀 있어 AI 가 그대로 옮기는 일이 있다(PR261015-3DA8C9).
 * 옵션에 같은 단위의 규격이 하나도 없으면(예: 색상만 있는 옵션) 손대지 않는다.
 * 옵션마다 값이 다르면(1.5/2.5/3.5cm) 한 옵션만 가진 값도 뺀다 — 모든 옵션에 같은 값일 때만 남는다(PR261017-D188A7).
 * 규격 한 덩어리 = 단일(14인치) · 범위(12-13.3인치) · 가로x세로(30x40cm). 덩어리째 유지하거나 덩어리째 뺀다.
 */
const UNIT_ALIASES = [
  { unit: 'inch', re: '(?:인치|寸|英寸|inch(?:es)?|")' },
  { unit: 'cm', re: '(?:cm|센치|센티|厘米)' },
  { unit: 'mm', re: '(?:mm|毫米)' },
  { unit: 'ml', re: '(?:ml|毫升)' }
];

const NUM = '\\d+(?:\\.\\d+)?';
// 앞 경계: 영문자·숫자·점 뒤에 붙은 숫자(A4, RTX3060)는 규격 시작으로 보지 않는다(소비하지 않는 lookbehind)
const PRE = '(?<![A-Za-z0-9.])';
// 덩어리: 가로x세로(x·×·*, 여러 번) 또는 범위(-·~·–·/) 또는 단일
const BODY = `(${NUM}(?:\\s*[x×*]\\s*${NUM})+|${NUM}(?:\\s*(?:-|~|–|/)\\s*${NUM})?)`;

function specRegex(alias) {
  // 그룹: 1=덩어리, 2=뒤따르는 구분자(뺄 때 같이 뺀다)
  return new RegExp(`${PRE}${BODY}\\s*${alias.re}(?![A-Za-z])(\\s*[,/·])?`, 'gi');
}

function isDims(body) {
  return /[x×*]/i.test(body);
}

function dimsKey(body) {
  return body.split(/\s*[x×*]\s*/i).map(n => String(Number(n))).join('x');
}

function rangeNums(body) {
  return body.split(/\s*(?:-|~|–|\/)\s*/).map(Number);
}

function optionTexts(options) {
  const out = [];
  (Array.isArray(options) ? options : []).forEach(o => {
    if (!o) return;
    ['option1', 'option2'].forEach(k => {
      if (o[k]) out.push(String(o[k].name || ''), String(o[k].nameTrans || ''));
    });
    if (o.korName) out.push(String(o.korName));
  });
  return out.join(' \n ');
}

// 단위별로 옵션에 있는 숫자(단일·범위 끝값)와 가로x세로 조합
function allowedByUnit(text) {
  const byUnit = {};
  UNIT_ALIASES.forEach(alias => {
    const re = specRegex(alias);
    let m;
    while ((m = re.exec(text)) !== null) {
      const slot = byUnit[alias.unit] || (byUnit[alias.unit] = { nums: new Set(), dims: new Set() });
      if (isDims(m[1])) slot.dims.add(dimsKey(m[1]));
      else rangeNums(m[1]).forEach(n => slot.nums.add(n));
    }
  });
  return byUnit;
}

// ── 옵션 한 줄의 길이 규격 — 견적서 사이즈 속성은 남긴 SKU 원문 값이 AI 추측보다 먼저(cpl-x3-skusizeguess-1) ──
// 보고: PR261021-761EC8(45–50mm → AI «40mm») · PR261021-C8F601(4규격이 모두 AI 대표값 «39*33*4cm»)
const LENGTH_ALIASES = UNIT_ALIASES.filter(a => a.unit !== 'ml');
const UNIT_TEXT = { cm: 'cm', mm: 'mm', inch: '인치' };
const SIZE_ATTR_RE = /사이즈|크기|규격|치수/;

function parseOptionValue(v) {
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch (e) { return { name: v }; }   // JSON 이 아닌 글자 그대로의 옵션명
}

function oneOptionText(option) {
  const o = option || {};
  const out = [];
  ['option1', 'option2'].forEach(k => {
    const v = parseOptionValue(o[k]);
    if (v) out.push(String(v.name || ''), String(v.nameTrans || ''));
  });
  if (o.korName) out.push(String(o.korName));
  return out.join(' \n ');
}

/**
 * 옵션 하나의 길이 규격 — 덩어리가 하나일 때만(같은 값이 원문·번역에 두 번 있어도 하나). 없거나 서로 다른 값 둘+ = null(애매하면 손 0)
 * @returns {{value:string, kind:'dims'|'range'|'single'}|null} value = «38*32*1.2cm» · «45-50mm» · «40mm»
 */
export function optionSizeSpec(option) {
  const text = oneOptionText(option);
  const found = new Map();
  LENGTH_ALIASES.forEach(alias => {
    const re = specRegex(alias);
    let m;
    while ((m = re.exec(text)) !== null) {
      const body = m[1];
      const kind = isDims(body) ? 'dims' : (/[-~–/]/.test(body) ? 'range' : 'single');
      const nums = kind === 'dims' ? body.split(/\s*[x×*]\s*/i).map(Number) : rangeNums(body);
      const value = nums.join(kind === 'dims' ? '*' : '-') + UNIT_TEXT[alias.unit];
      found.set(value, { value, kind });
    }
  });
  return found.size === 1 ? [...found.values()][0] : null;
}

/**
 * 사이즈 계열 노출속성(이름 사이즈·크기·규격·치수)을 그 옵션의 원문 규격으로 바꾼다.
 * 숫자칸(unitMeta 에 이름 있음)은 단일 값만 넣는다 — 범위·가로x세로는 쿠팡이 거절할 수 있어 AI 값 그대로.
 * 원소는 새 객체로 바꿔 끼운다 — mergeAttributes 가 공통 속성 객체를 옵션끼리 같이 쓴다(얕은 복사).
 * productFeature = 옵션에 규격이 없을 때 쓰는 상품 특징(규격이 하나일 때만)
 * @returns {{replaced:Array<{name,from,to}>, guessed:string[]}} guessed = 원문 값을 못 넣어 AI 추정이 남은 속성 이름
 */
export function applySkuSize(exposedAttributes, option, unitMeta = {}, productFeature = '') {
  const out = { replaced: [], guessed: [] };
  if (!Array.isArray(exposedAttributes)) return out;
  let spec;
  exposedAttributes.forEach((a, i) => {
    if (!a || !SIZE_ATTR_RE.test(String(a.attributeName || ''))) return;
    // 옵션에 규격이 없으면 상품 특징에 사람이 적은 규격 하나(8642DA «약 12x6x17.5cm» 인데 4옵션 모두 AI «28.5*18*20cm»)
    if (spec === undefined) spec = optionSizeSpec(option) || (productFeature ? optionSizeSpec({ korName: String(productFeature) }) : null);
    const name = a.attributeName;
    if (!spec || (unitMeta && unitMeta[name] && spec.kind !== 'single')) { out.guessed.push(name); return; }
    if (a.attributeValue !== spec.value) out.replaced.push({ name, from: a.attributeValue, to: spec.value });
    exposedAttributes[i] = { ...a, attributeValue: spec.value };
  });
  return out;
}

/** 견적서 공통 프롬프트용 «남긴 옵션 원문» — 옵션마다 한 줄(원문·번역·한글명) · 옵션 30개·줄 160자까지 */
export function optionSourceLines(options) {
  const list = Array.isArray(options) ? options : [];
  const lines = list.slice(0, 30).map((o, i) => {
    const text = oneOptionText(o).split(' \n ').map(s => s.trim()).filter(Boolean);
    return `- 옵션 ${i + 1}: ${[...new Set(text)].join(' / ').slice(0, 160) || '(원문 없음)'}`;
  });
  if (list.length > 30) lines.push(`- … 외 ${list.length - 30}개`);
  return lines.length ? lines.join('\n') : '(옵션 원문 없음)';
}

function isAllowed(body, slot) {
  if (isDims(body)) return slot.dims.has(dimsKey(body));
  return rangeNums(body).every(n => slot.nums.has(n));
}

// 옵션 한 줄마다 단위별 규격 — 그 단위가 적힌 줄만 모은다
function rowsByUnit(options) {
  const byUnit = {};
  (Array.isArray(options) ? options : []).filter(Boolean).forEach(o => {
    const row = allowedByUnit(optionTexts([o]));
    Object.keys(row).forEach(u => (byUnit[u] || (byUnit[u] = [])).push(row[u]));
  });
  return byUnit;
}

// 옵션마다 값이 다른데(1.5/2.5/3.5cm) 단일 값·가로x세로 하나를 상품명에 박으면 한 옵션의 규격으로 고정된다(PR261017-D188A7).
// 그 단위가 적힌 옵션 줄이 둘 이상이고, 상품명의 값들이 옵션 줄을 다 덮지 못하면(일부만 적음) 모든 줄에 있는 값만 남긴다.
// 옵션 값을 다 적은 것(«(14cm/20cm)» · 옵션 14/20)과 범위(1.5-3.5cm)는 묶어 말한 것이라 그대로 둔다.
function titleCoversRows(name, alias, slot, rows) {
  const nums = new Set();
  const dims = new Set();
  const re = specRegex(alias);
  let m;
  while ((m = re.exec(name)) !== null) {
    if (!isAllowed(m[1], slot)) continue;
    if (isDims(m[1])) dims.add(dimsKey(m[1]));
    else rangeNums(m[1]).forEach(n => nums.add(n));
  }
  return rows.every(r => [...r.nums].some(n => nums.has(n)) || [...r.dims].some(d => dims.has(d)));
}

function pinsOneOption(body, rows, covered) {
  if (covered || !rows || rows.length < 2) return false;
  if (isDims(body)) return !rows.every(r => r.dims.has(dimsKey(body)));
  const nums = rangeNums(body);
  if (nums.length !== 1) return false;
  return !rows.every(r => r.nums.has(nums[0]));
}

function tidy(name) {
  return name
    // 괄호 안쪽에 남은 구분자("(14cm/)") 정리
    .replace(/\s*[,/·]\s*([)\]}】])/g, '$1')
    .replace(/([([{【])\s*[,/·]\s*/g, '$1')
    .replace(/\(\s*\)|\[\s*\]|\{\s*\}|【\s*】/g, '')
    .replace(/(\s*[,/·]\s*){2,}/g, ', ')
    .replace(/\s+([,/·])/g, '$1')
    .replace(/^[\s,/·]+|[\s,/·]+$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * @param {string} productName - AI 가 만든 상품명
 * @param {Array} options - 남긴 옵션(product.options)
 * @returns {string} 옵션에 없는 규격을 뺀 상품명
 */
export function dropSpecsNotInOptions(productName, options) {
  if (!productName || typeof productName !== 'string') return productName;
  const allowed = allowedByUnit(optionTexts(options));
  const rows = rowsByUnit(options);
  let name = productName;
  let changed = false;
  UNIT_ALIASES.forEach(alias => {
    const slot = allowed[alias.unit];
    if (!slot || (!slot.nums.size && !slot.dims.size)) return;
    const unitRows = rows[alias.unit];
    const covered = !unitRows || titleCoversRows(name, alias, slot, unitRows);
    name = name.replace(specRegex(alias), (whole, body) => {
      if (isAllowed(body, slot) && !pinsOneOption(body, unitRows, covered)) return whole;
      changed = true;
      return '';
    });
  });
  if (!changed) return productName;
  const cleaned = tidy(name);
  if (!cleaned) return productName;
  console.log(`SEO 상품명 규격 정리(남긴 옵션에 없는 규격 제거): "${productName}" > "${cleaned}"`);
  return cleaned;
}

// 옵션 한 줄의 글자(원문·번역·한글 옵션명)를 소문자·공백 하나로 모은다
function norm(t) {
  return String(t || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// 영문·숫자 토큰은 단어 경계로만 맞춘다("(M)" 이 "mint" 에 걸려 지워지지 않게)
function hasToken(row, t) {
  if (!/^[a-z0-9]+$/.test(t)) return row.includes(t);
  return new RegExp('(^|[^a-z0-9])' + t + '([^a-z0-9]|$)').test(row);
}

function optionRowText(o, korByNumber) {
  if (!o) return '';
  const parts = [];
  ['option1', 'option2'].forEach(k => {
    if (o[k]) parts.push(o[k].name, o[k].nameTrans);
  });
  parts.push(o.korName);
  if (o.optionNumber != null && korByNumber[o.optionNumber]) parts.push(korByNumber[o.optionNumber]);
  return norm(parts.filter(Boolean).join(' | '));
}

/**
 * 상품명 괄호 안이 옵션 값 일부만 나열한 것이면 괄호째 뺀다(PR261016-FCD580).
 * 예: 색상 50개 상품명 "... (암적색/암적색 체크)" → 괄호 제거.
 * 괄호 안 값이 전부 옵션 값이고, 그 값들 중 어느 것도 갖지 않은 옵션이 하나라도 있으면 "일부"로 본다.
 * 옵션 값이 아닌 괄호(예: "(2개입)")나 모든 옵션을 덮는 괄호는 그대로 둔다.
 * @param {string} productName
 * @param {Array} options - 전체 옵션(product.options)
 * @param {Array} [translatedOptions] - AI 번역 옵션({optionNumber, korName})
 * @returns {string}
 */
export function dropPartialOptionParens(productName, options, translatedOptions) {
  if (!productName || typeof productName !== 'string') return productName;
  const list = Array.isArray(options) ? options.filter(Boolean) : [];
  if (list.length < 2) return productName;
  const korByNumber = {};
  (Array.isArray(translatedOptions) ? translatedOptions : []).forEach(t => {
    if (t && t.optionNumber != null && t.korName) korByNumber[t.optionNumber] = t.korName;
  });
  const rows = list.map(o => optionRowText(o, korByNumber));
  let changed = false;
  const name = productName.replace(/\(([^()]*)\)|\[([^[\]]*)\]|【([^【】]*)】/g, (whole, a, b, c) => {
    const tokens = String(a || b || c || '').split(/[/,·|]/).map(norm).filter(Boolean);
    if (!tokens.length) return whole;
    const allAreOptionValues = tokens.every(t => rows.some(r => hasToken(r, t)));
    if (!allAreOptionValues) return whole;
    const someRowUncovered = rows.some(r => !tokens.some(t => hasToken(r, t)));
    if (!someRowUncovered) return whole;
    changed = true;
    return '';
  });
  if (!changed) return productName;
  const cleaned = tidy(name);
  if (!cleaned) return productName;
  console.log(`SEO 상품명 정리(옵션 일부만 적은 괄호 제거): "${productName}" > "${cleaned}"`);
  return cleaned;
}

/**
 * 프롬프트용 전체 옵션 요약 — 옵션1·옵션2 별 서로 다른 값 목록(번역 우선).
 * @param {Array} options
 * @param {number} [maxPerAxis=60]
 * @returns {string} 예: "옵션1(12종): 블랙, 화이트, ...
옵션2(3종): S, M, L"
 */
export function summarizeOptionValues(options, maxPerAxis = 60) {
  const list = Array.isArray(options) ? options.filter(Boolean) : [];
  const lines = [];
  ['option1', 'option2'].forEach((k, i) => {
    const seen = new Set();
    list.forEach(o => {
      const v = o[k] && String(o[k].nameTrans || o[k].name || '').trim();
      if (v) seen.add(v);
    });
    if (!seen.size) return;
    const vals = [...seen];
    const more = vals.length > maxPerAxis ? ` 외 ${vals.length - maxPerAxis}종` : '';
    lines.push(`옵션${i + 1}(${vals.length}종): ${vals.slice(0, maxPerAxis).join(', ')}${more}`);
  });
  return lines.join('\n');
}

/**
 * 옵션이 많아 등록건을 나눌 때(분할) 나뉜 상품의 이름을 만든다.
 * 상품명은 나누기 전 전체 옵션 기준으로 한 번 만들어지므로, 그 분할이 가진 옵션에 없는 규격은 빼고 붙인다(PR261016-0BC6A1).
 * @param {string} baseName - 나누기 전 상품명
 * @param {Array} groupOptions - 이 분할이 가진 옵션
 * @param {string} attrText - 이름 뒤에 붙일 구분 속성
 * @returns {string}
 */
export function splitProductName(baseName, groupOptions, attrText) {
  const name = dropSpecsNotInOptions(baseName, groupOptions);
  // 겹치는 옵션만 뺀 분리에서 원본 상품(그룹 1)은 꼬리말 없이 둔다
  return String(attrText || '').trim() ? `${name}, ${attrText}` : name;
}

/**
 * 어린이용 낱말(어린이·아동·키즈·유아 …) — 1688 원문·연관 키워드에서 AI 가 옮겨 오면 일반 상품이
 * 어린이제품(KC 인증 대상)처럼 보인다(오픈이슈 #144 · PR261016-89F9B8). 등록 카테고리가 어린이용이 아닐 때만 뺀다.
 */
const KIDS_CATEGORY_RE = /유아|아동|키즈|완구|어린이|출산|베이비|아기/;
const KIDS_WORD = '(?:유아동|어린이|아동|키즈|유아|kids)(?:용|전용)?';
const KIDS_PRE = '(^|[\\s,/·(\\[])';
// 아기·베이비 — 대상 근거(특징·대상 키워드)가 «성인» 일 때만 · 낱말 하나 통째일 때만(붙은 «베이비핑크»·«아기자기»는 다른 낱말 · kidswordbaby-1)
//   카테고리 까닭만으론 안 뺀다 — «베이비 파우더»·«베이비 오일» 은 품목 이름
const KIDS_END = '(?=$|[\\s,/·)\\]])';
const BABY_WORD = '(?:아기|베이비)(?:용|전용)?';

/** @param {string} categoryPath - 등록 카테고리 경로(예: "ROOT>출산/유아동>…") @returns {boolean} */
export function isKidsCategory(categoryPath) {
  return KIDS_CATEGORY_RE.test(String(categoryPath || ''));
}

/**
 * @param {string} productName - AI 가 만든 상품명
 * @param {string} categoryPath - 등록 카테고리 경로. 비었으면(모르면) 손대지 않는다
 * @returns {{ name: string, removed: string[] }} 뺀 상품명과 뺀 낱말
 */
export function stripKidsWords(productName, categoryPath) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  if (!String(categoryPath || '').trim() || isKidsCategory(categoryPath)) return { name: productName, removed: [] };
  const removed = [];
  const keep = (m) => { removed.push(m.trim()); };
  let name = productName
    // 낱말 하나 통째(어린이 · 키즈용 · (아동))
    .replace(new RegExp(`${KIDS_PRE}(${KIDS_WORD})(?=$|[\\s,/·)\\]])`, 'gi'), (all, pre, word) => { keep(word); return pre; })
    // 붙여 쓴 앞머리(키즈모양펀치 → 모양펀치). 남는 말이 한 글자면(어린이집) 다른 낱말이라 두지 않는다
    .replace(new RegExp(`${KIDS_PRE}(${KIDS_WORD})(?=[가-힣A-Za-z0-9]{2,})`, 'gi'), (all, pre, word) => { keep(word); return pre; });
  if (!removed.length) return { name: productName, removed };
  name = tidy(name);
  return { name, removed };
}

/**
 * 대상(성인·아동) — 1688 원 제목의 «儿童成人»을 AI 가 «성인 아동 공용»으로 옮긴다(PR261017-19D39A).
 * 근거 = 상품 특징(사용자가 적은 글) + 대상 키워드(«성인용 고무줄» · kidswordbaby-1). 한쪽 대상만 말하면 상품명의 다른 쪽 대상 낱말을 빼고 «성인 공용» → «성인용» 으로 줄인다.
 * 대상을 말하지 않거나 양쪽 다 말하면 손대지 않는다(카테고리 기준은 stripKidsWords).
 */
const ADULT_WORD = '(?:성인|어른)(?:용|전용)?';
const FEATURE_KIDS_RE = /어린이|아동|키즈|유아|아기|베이비|초등|kids/i;
const FEATURE_ADULT_RE = /성인|어른/;

/**
 * @param {string} productName - AI 가 만든 상품명
 * @param {string} productFeature - 상품 특징
 * @param {string} [targetKeyword] - 대상 키워드
 * @returns {{ name: string, removed: string[] }}
 */
export function dropAudienceNotInFeature(productName, productFeature, targetKeyword) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  const feat = `${featureWithoutBans(productFeature)} ${String(targetKeyword || '')}`;
  const adult = FEATURE_ADULT_RE.test(feat);
  const kids = FEATURE_KIDS_RE.test(feat);
  if (adult === kids) return { name: productName, removed: [] };
  const drop = adult ? KIDS_WORD : ADULT_WORD;
  const dropWhole = adult ? `${KIDS_WORD}|${BABY_WORD}` : ADULT_WORD;
  const keepWord = adult ? '성인' : '아동';
  const removed = [];
  const keep = (all, pre, word) => { removed.push(word.trim()); return pre; };
  let name = productName
    .replace(new RegExp(`${KIDS_PRE}(${dropWhole})${KIDS_END}`, 'gi'), keep)
    .replace(new RegExp(`${KIDS_PRE}(${drop})(?=[가-힣A-Za-z0-9]{2,})`, 'gi'), keep);
  if (!removed.length) return { name: productName, removed };
  // 남은 «성인 공용·성인/겸용» → «성인용»
  name = name.replace(new RegExp(`(^|\\s)${keepWord}(?:용)?[\\s,/·]*(?:공용|겸용)(?=$|\\s)`, 'g'), `$1${keepWord}용`);
  name = tidy(name);
  if (!name) return { name: productName, removed: [] };
  return { name, removed };
}

/**
 * 개수(9개입·4구·12pcs …) — 1688 원문 제목의 개수를 AI 가 옮겨 오면 실제로 남긴 구성과 다른 개수가 상품명에 들어간다
 * (PR261016-423097: 12支 옵션만 남겼는데 «9개입» · PR261016-51A981: 2갈래×4개인데 «4구»).
 * 같은 묶음끼리만 비교한다 — 낱개 수(개·개입·pcs·支·个·세트 — 맨 «p» 는 1080p·퍼즐 4p 와 겹쳐 뺐다)와 갈래 수(구·갈래·钩)는 서로 다른 뜻이다.
 * 근거 글자(남긴 옵션명·상품 특징)에 그 묶음 개수가 하나도 없으면 손대지 않는다(dropSpecsNotInOptions 와 같은 원칙).
 */
// 장 무리(매·장·张·片·枚·抽(물티슈 100抽) — AD1637 단품 장갑 «100매»)는 낱개 수와 근거를 같이 본다(10片装 = 10매 = 10개입). 장갑·매트처럼 한글이 이어지면 수가 아니다
const COUNT_GROUPS = [
  { group: 'piece', re: '(?:개입|개(?!월)|입(?![가-힣])|pcs|pc|支|个|個|件|套|세트|팩)' },
  { group: 'sheet', re: '(?:매(?![가-힣])|장(?![가-힣])|张|片|枚|抽)' },
  { group: 'prong', re: '(?:구|갈래|钩|頭|头)' }
];

function countRegex(g) {
  // 그룹: 1=숫자, 2=뒤따르는 구분자(뺄 때 같이 뺀다). 영문 단위 뒤에 영문자가 이어지면(4Pro) 개수가 아니다
  return new RegExp(`${PRE}(\\d+)\\s*${g.re}(?![A-Za-z])(\\s*[,/·])?`, 'gi');
}

// 中 숫자 + 단위(三个装 → 3个装). 단위가 안 붙은 三通·双头 같은 낱말은 그대로 둔다
const CN_NUM = { 一: 1, 两: 2, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9, 十: 10 };
function cnDigits(text) {
  return text.replace(/([一两二三四五六七八九十])(?=\s*(?:个|個|只|支|件|套|包|装|入|片))/g, (m, c) => String(CN_NUM[c]));
}

function countsByGroup(text) {
  const byGroup = {};
  COUNT_GROUPS.forEach(g => {
    const re = countRegex(g);
    let m;
    while ((m = re.exec(text)) !== null) {
      (byGroup[g.group] || (byGroup[g.group] = new Set())).add(Number(m[1]));
    }
  });
  return byGroup;
}

/**
 * @param {string} productName - AI 가 만든 상품명
 * @param {Array} options - 남긴 옵션(product.options)
 * @param {string} [productFeature] - 사용자가 적은 상품 특징(구성·수량)
 * @param {number} [bundleQuantity] - 묶음 수량(sourcingBundleQuantity). 옵션·특징에 낱개 수가 없으면 이것만 근거로 본다
 * @returns {{ name: string, removed: string[] }} 근거에 없는 개수를 뺀 상품명과 뺀 글자
 *
 * 근거는 옵션 원문·영문 번역·상품 특징뿐이다. AI 옵션 번역(korName)은 같은 AI 의 출력이라 근거가 아니다
 * (PR261016-F66347: 三通 «3방향» 을 «3개» 로 번역 → 그 번역이 상품명 «3개» 를 정당화). 남긴 옵션 개수도 개수가 아니다.
 */
export function dropCountsNotInSource(productName, options, productFeature, bundleQuantity) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  const raw = [];
  (Array.isArray(options) ? options : []).forEach(o => {
    if (!o) return;
    ['option1', 'option2'].forEach(k => { if (o[k]) raw.push(String(o[k].name || ''), String(o[k].nameTrans || '')); });
  });
  raw.push(String(productFeature || ''));
  const allowed = countsByGroup(cnDigits(raw.join(' \n ')));
  const bq = Number(bundleQuantity);
  const each = new Set([...(allowed.piece || []), ...(allowed.sheet || [])]);
  if (!each.size && Number.isInteger(bq) && bq >= 1) each.add(bq);
  allowed.piece = each;
  allowed.sheet = each;
  const removed = [];
  let name = productName;
  COUNT_GROUPS.forEach(g => {
    const nums = allowed[g.group];
    if (!nums || !nums.size) return;
    name = name.replace(countRegex(g), (whole, n) => {
      if (nums.has(Number(n))) return whole;
      removed.push(whole.replace(/[\s,/·]+$/, '').trim());
      return '';
    });
  });
  if (!removed.length) return { name: productName, removed };
  const cleaned = tidy(name);
  if (!cleaned) return { name: productName, removed: [] };
  return { name: cleaned, removed };
}

/**
 * 이름에 낱개 수 N(N개·N개입·N세트·N개 세트·N팩 …)이 이미 있나 — 묶음 수 표기 잣대 하나.
 * 상품명(ensureBundleCount)·옵션명 «(N개묶음)»(bundleOptionService.withBundleSuffix)·한글표시 제품명·허브 상품명이 같이 쓴다
 * (윈 3452 mircompany PR261021-56DBDD «2개 세트 …2세트 (2개묶음)»).
 */
export function hasBundleCount(name, bundleQuantity) {
  const n = Number(bundleQuantity);
  if (!name || typeof name !== 'string' || !Number.isInteger(n)) return false;
  const piece = COUNT_GROUPS.find(g => g.group === 'piece');
  let has = false;
  name.replace(countRegex(piece), (whole, num) => { if (Number(num) === n) has = true; return whole; });
  return has;
}

/**
 * 묶음 수량(N개를 한 상품으로 파는 것)을 상품명에 붙인다 — F07A65. AI 가 묶음 수를 안 쓰고, 개수 뒤처리(dropCountsNotInSource)는 빼기만 한다.
 * 상품명에 낱개 수 N(N개·N개입·N세트·N팩 …)이 이미 있으면 그대로, 없으면 끝에 « N개». 다른 수(10개입)는 안 건드린다(«10개입 2개»).
 * 붙인 이름이 상한(maxLength)을 넘으면 붙이지 않고 skipped 로 알린다.
 * @param {number} bundleQuantity - 묶음 수량. 2 미만·숫자 아님이면 아무것도 안 한다
 * @returns {{ name: string, added: string, skipped: string }}
 */
export function ensureBundleCount(productName, bundleQuantity, maxLength = 70) {
  const n = Number(bundleQuantity);
  if (!productName || typeof productName !== 'string' || !Number.isInteger(n) || n < 2) return { name: productName, added: '', skipped: '' };
  if (hasBundleCount(productName, n)) return { name: productName, added: '', skipped: '' };
  const name = `${productName.trim()} ${n}개`;
  if (name.length > maxLength) return { name: productName, added: '', skipped: `${n}개` };
  return { name, added: `${n}개`, skipped: '' };
}

/**
 * 수납 용량(可放200张 = 200장 들어감)을 상품명에 «200매»로만 쓰면 종이 200매를 파는 것처럼 보인다(PR261016-BD2F9B).
 * 남긴 옵션·상품 특징에 «수납 용량»으로 적힌 매수만, 상품명에서 뜻(수납)이 안 붙은 채 나오면 «N매 수납»으로 바꾼다.
 * @returns {{ name: string, marked: string[] }}
 */
const CAPACITY_RES = [
  /(?:可放|可装|可收纳|容纳|收纳|수납|보관)\s*(\d+)\s*(?:张|页|매|장)/g,
  /(\d+)\s*(?:张|매|장)\s*(?:수납|보관|용량)/g
];

export function markCapacityCounts(productName, options, productFeature) {
  if (!productName || typeof productName !== 'string') return { name: productName, marked: [] };
  const sourceText = `${optionTexts(options)} \n ${String(productFeature || '')}`;
  const caps = new Set();
  CAPACITY_RES.forEach(re => {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(sourceText)) !== null) caps.add(Number(m[1]));
  });
  if (!caps.size) return { name: productName, marked: [] };
  const marked = [];
  const name = productName.replace(new RegExp(`${PRE}(\\d+)\\s*(?:매|장)(?![가-힣]*\\s*(?:수납|보관|용량))`, 'g'), (whole, n) => {
    if (!caps.has(Number(n))) return whole;
    marked.push(whole);
    return `${n}매 수납`;
  });
  return { name, marked };
}

/**
 * 형태 낱말 — 1688 원 제목의 형태(半指 반손가락)가 남긴 옵션의 형태(大半掌 손바닥 길이)와 다르면 상품명에서 뺀다(PR261016-BD2F9B).
 * 원 제목에 그 형태가 있고, 남긴 옵션 원문에는 그 형태가 없고 다른 형태가 적혀 있을 때만 손댄다.
 */
const SHAPE_CONFLICTS = [
  { title: /半指|露指/, other: /半掌|全指/, words: [[/반\s*손가락/g, ''], [/핑거리스/g, ''], [/반장갑/g, '장갑']] }
];

/**
 * @param {string} productName - AI 가 만든 상품명
 * @param {string} originalTitle - 1688 원 제목
 * @param {Array} options - 남긴 옵션(원문 option1/option2.name 만 본다 — 번역은 원 제목에 끌려갈 수 있다)
 * @returns {{ name: string, removed: string[] }}
 */
export function dropShapeWordsNotInOptions(productName, originalTitle, options) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  const raw = (Array.isArray(options) ? options : []).filter(Boolean)
    .map(o => [o.option1 && o.option1.name, o.option2 && o.option2.name].filter(Boolean).join(' ')).join(' \n ');
  const removed = [];
  let name = productName;
  SHAPE_CONFLICTS.forEach(c => {
    if (!c.title.test(String(originalTitle || '')) || c.title.test(raw) || !c.other.test(raw)) return;
    c.words.forEach(([re, to]) => {
      name = name.replace(re, (m) => { removed.push(m); return to; });
    });
  });
  if (!removed.length) return { name: productName, removed };
  const cleaned = tidy(name);
  if (!cleaned) return { name: productName, removed: [] };
  return { name: cleaned, removed };
}

/**
 * 색 낱말 — 1688 원 제목의 색(金色 골드)이 남긴 옵션의 색(钢色 스틸)과 다르면 상품명·검색태그에서 뺀다(PR261017-125A33).
 * 원 제목에 그 색이 있고, 남긴 옵션 원문에는 그 색이 없고 다른 색이 적혀 있을 때만 손댄다(옵션에 색이 없으면 그대로).
 * 바꿔 넣지 않고 빼기만 한다 — 옵션 색을 다른 말로 지어내지 않는다.
 */
const COLOR_WORDS = [
  { key: 'gold', zh: /(?<!玫瑰)金色/, words: ['골드', '금색'], kwWords: ['골드', '금'], notAfter: ['로즈', '로즈 '] },
  { key: 'rosegold', zh: /玫瑰金/, words: ['로즈골드', '로즈 골드'], kwWords: ['로즈골드'] },
  { key: 'silver', zh: /银色/, words: ['실버', '은색'], kwWords: ['실버'] },
  { key: 'steel', zh: /钢色|钢本色/, words: [], kwWords: [] },
  { key: 'black', zh: /黑色/, words: ['블랙', '검정색', '검정'], kwWords: ['블랙'] },
  { key: 'white', zh: /白色/, words: ['화이트', '흰색'], kwWords: ['화이트'] },
  { key: 'red', zh: /红色/, words: ['레드', '빨간색'], kwWords: ['레드'] },
  { key: 'pink', zh: /粉色/, words: ['핑크', '분홍색', '분홍'], kwWords: ['핑크'] },
  { key: 'blue', zh: /蓝色/, words: ['블루', '파란색'], kwWords: ['블루'] },
  { key: 'green', zh: /绿色/, words: ['그린', '초록색', '초록'], kwWords: ['그린'] }
];

// 낱말 경계: 앞뒤가 한글·영문이 아니어야 독립 낱말이다(블루투스·그린티·레드와인·화이트보드 같은 복합어는 건드리지 않는다)
const WORD_EDGE = '[가-힣A-Za-z]';
function standaloneRe(word, notAfter) {
  const guard = (notAfter || []).map(p => `(?<!${p.trim()}\\s?)`).join('');
  return new RegExp(`${guard}(?<!${WORD_EDGE})${word.replace(/\s/g, '\\s?')}(?:\\s?(?:색상?|컬러))?(?!${WORD_EDGE})`, 'g');
}

// 검색태그: 색 낱말 + 상품명 낱말 끝말(골드+팔찌 ← 뱅글팔찌)일 때만 그 색의 태그로 본다(블루+투스 같은 복합어는 아님)
function isColorKeyword(kw, color, nameTokens) {
  const k = String(kw || '').replace(/\s+/g, '');
  if (!k) return false;
  return color.kwWords.some(w => {
    let i = k.indexOf(w);
    while (i >= 0) {
      const before = k.slice(0, i);
      const blocked = (color.notAfter || []).some(p => before.endsWith(p.replace(/\s+/g, '')));
      const rest = k.slice(i + w.length);
      if (!blocked) {
        if (!rest && !before) return true;
        if (rest.length >= 2 && nameTokens.some(t => t.length > rest.length && t.endsWith(rest) && !t.endsWith(w + rest))) return true;
        if (rest.length >= 2 && nameTokens.includes(rest)) return true;
      }
      i = k.indexOf(w, i + 1);
    }
    return false;
  });
}

/**
 * @param {string} productName - AI 가 만든 상품명
 * @param {Array} keywords - AI 가 만든 검색태그
 * @param {string} originalTitle - 1688 원 제목
 * @param {Array} options - 남긴 옵션(원문 option1/option2.name 만 본다)
 * @returns {{ name: string, keywords: Array, removed: string[] }} removed 는 실제로 뺀 낱말·태그만 담는다
 */
export function dropColorsNotInOptions(productName, keywords, originalTitle, options) {
  const kws = Array.isArray(keywords) ? keywords : [];
  const none = { name: productName, keywords: kws, removed: [] };
  if (!productName || typeof productName !== 'string') return none;
  const raw = (Array.isArray(options) ? options : []).filter(Boolean)
    .map(o => [o.option1 && o.option1.name, o.option2 && o.option2.name].filter(Boolean).join(' ')).join(' \n ');
  const title = String(originalTitle || '');
  if (!COLOR_WORDS.some(c => c.zh.test(raw))) return none;
  const leaked = COLOR_WORDS.filter(c => c.words.length && c.zh.test(title) && !c.zh.test(raw));
  if (!leaked.length) return none;
  const nameRemoved = [];
  let name = productName;
  leaked.forEach(c => c.words.forEach(w => {
    name = name.replace(standaloneRe(w, c.notAfter), (m) => {
      nameRemoved.push(m.trim());
      return ' ';
    });
  }));
  // 빼고 나서 이름이 비면 상품명은 그대로 두고, 뺐다고 하지 않는다
  let cleaned = nameRemoved.length ? tidy(name) : productName;
  if (!cleaned) { cleaned = productName; nameRemoved.length = 0; }
  const nameTokens = productName.split(/[\s,/·()[\]]+/).filter(Boolean);
  const kwRemoved = [];
  const keptKws = kws.filter(k => {
    if (!leaked.some(c => isColorKeyword(k, c, nameTokens))) return true;
    kwRemoved.push(String(k));
    return false;
  });
  const removed = [...nameRemoved, ...kwRemoved];
  if (!removed.length) return none;
  return { name: cleaned, keywords: keptKws, removed };
}

/**
 * 겹 수 낱말 — 1688 원 제목의 겹 수(双层·双面 이중)가 남긴 옵션의 겹 수(单层 단층)와 다르면 상품명·검색태그에서 뺀다(PR261017-24F4A9 «때수건 … 이중구조» · 옵션 优质单层).
 * 원 제목에 이중이 있고, 남긴 옵션 원문에는 이중이 없고 단층이 적혀 있을 때만 손댄다. 바꿔 넣지 않고 빼기만 한다.
 */
const LAYER_TITLE_RE = /双层|双面|两层|双重/;
const LAYER_SINGLE_RE = /单层|单面|一层|单片/;
const LAYER_WORD_RE = /이중\s*구조|이중|(?<!\d)2\s*중|더블\s*레이어|더블|양면|두\s*겹/g;

/**
 * @param {string} productName - AI 가 만든 상품명
 * @param {Array} keywords - AI 가 만든 검색태그
 * @param {string} originalTitle - 1688 원 제목
 * @param {Array} options - 남긴 옵션(원문 option1/option2.name 만 본다)
 * @returns {{ name: string, keywords: Array, removed: string[] }} removed 는 실제로 뺀 낱말·태그만 담는다
 */
export function dropLayerWordsNotInOptions(productName, keywords, originalTitle, options) {
  const kws = Array.isArray(keywords) ? keywords : [];
  const none = { name: productName, keywords: kws, removed: [] };
  if (!productName || typeof productName !== 'string') return none;
  const raw = (Array.isArray(options) ? options : []).filter(Boolean)
    .map(o => [o.option1 && o.option1.name, o.option2 && o.option2.name].filter(Boolean).join(' ')).join(' \n ');
  if (!LAYER_TITLE_RE.test(String(originalTitle || '')) || LAYER_TITLE_RE.test(raw) || !LAYER_SINGLE_RE.test(raw)) return none;
  const nameRemoved = [];
  const name = productName.replace(LAYER_WORD_RE, (m) => { nameRemoved.push(m); return ' '; });
  // 빼고 나서 이름이 비면 상품명은 그대로 두고, 뺐다고 하지 않는다
  let cleaned = nameRemoved.length ? tidy(name) : productName;
  if (!cleaned) { cleaned = productName; nameRemoved.length = 0; }
  const kwRemoved = [];
  const keptKws = kws.filter(k => {
    if (!new RegExp(LAYER_WORD_RE.source).test(String(k || ''))) return true;
    kwRemoved.push(String(k));
    return false;
  });
  const removed = [...nameRemoved, ...kwRemoved];
  if (!removed.length) return none;
  return { name: cleaned, keywords: keptKws, removed };
}

/**
 * 공급사 품번(F1467·FZ-130) — 옵션 원문·1688 货号 에 있는 영문+숫자 코드가 상품명에 들어가면 뺀다(PR261016-8C6EFC «곱창밴드 (F1467)»).
 * 코드는 영문 1~3자 + (하이픈) + 숫자 3자리 이상만 본다 — 1080p·A3·8K 같은 규격은 해당하지 않는다.
 * @param {string} productName
 * @param {Array} options - 남긴 옵션(원문 option1/option2.name)
 * @param {Array} [attributes] - 1688 속성({attributeName, value})
 * @returns {{ name: string, removed: string[] }}
 */
export function dropItemCodes(productName, options, attributes) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  const raw = [
    (Array.isArray(options) ? options : []).filter(Boolean)
      .map(o => [o.option1 && o.option1.name, o.option2 && o.option2.name].filter(Boolean).join(' ')).join(' '),
    (Array.isArray(attributes) ? attributes : []).filter(a => a && /货号|型号/.test(String(a.attributeName || ''))).map(a => a.value).join(' ')
  ].join(' ');
  const codes = new Set((raw.match(/[A-Za-z]{1,3}-?\d{3,}/g) || []).map(c => c.toUpperCase()));
  if (!codes.size) return { name: productName, removed: [] };
  const removed = [];
  const name = productName.replace(/[([【]?\s*([A-Za-z]{1,3}-?\d{3,})\s*[)\]】]?/g, (whole, code) => {
    if (!codes.has(code.toUpperCase())) return whole;
    removed.push(code);
    return ' ';
  });
  if (!removed.length) return { name: productName, removed };
  const cleaned = tidy(name);
  if (!cleaned) return { name: productName, removed: [] };
  return { name: cleaned, removed };
}

// CJK 한자(간체·번체·확장A·호환) — 한국어 상품명·옵션명에 남으면 안 된다(PR261016-D729B4 «紗窗»)
const HAN_RE = /[㐀-䶿一-鿿豈-﫿]+/g;

export function hasHan(text) {
  return typeof text === 'string' && /[㐀-䶿一-鿿豈-﫿]/.test(text);
}

/**
 * 남은 한자 덩어리를 뺀다(번역은 앞 단계 AI 몫 — 여기는 통과시키지 않는 마지막 그물).
 * @param {string} text
 * @returns {{ text: string, removed: string[] }}
 */
export function stripHan(text) {
  if (!text || typeof text !== 'string') return { text, removed: [] };
  const removed = text.match(HAN_RE) || [];
  if (!removed.length) return { text, removed: [] };
  return { text: tidy(text.replace(HAN_RE, ' ')), removed };
}

/**
 * 품목 낱말 — 대상 키워드·1688 제품 카테고리가 가리키는 품목과 서로 다른 품목 낱말이 상품명에 들어가면 뺀다
 * (PR261016-6E43DF: 바라클라바인데 «… 스포츠 헤어밴드» — 원 제목 头巾·연관 키워드에서 옴).
 * 근거(대상 키워드·카테고리 속성)에 그 품목이 있고, 충돌 낱말은 근거에 없을 때만 손댄다.
 */
const ITEM_CONFLICTS = [
  { item: /바라클라바|발라클라바|발라크라바|복면|페이스\s*마스크|넥워머|넥\s*게이터|라이딩\s*마스크/, words: /헤어\s*밴드|머리\s*띠|헤어\s*띠/g }
];

/**
 * @param {string} productName
 * @param {string} targetKeyword
 * @param {Array} [attributes] - 1688 속성(제품 카테고리 값도 근거)
 * @returns {{ name: string, removed: string[] }}
 */
export function dropConflictingItemWords(productName, targetKeyword, attributes) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  const basis = [String(targetKeyword || ''), ...(Array.isArray(attributes) ? attributes : [])
    .filter(a => a && /카테고리|类别|category/i.test(`${a.attributeName || ''} ${a.attributeNameTrans || ''}`))
    .map(a => `${a.value || ''} ${a.valueTrans || ''}`)].join(' ');
  const removed = [];
  let name = productName;
  ITEM_CONFLICTS.forEach(c => {
    c.words.lastIndex = 0;
    if (!c.item.test(basis) || new RegExp(c.words.source).test(basis)) return;
    name = name.replace(c.words, (m) => { removed.push(m); return ''; });
  });
  if (!removed.length) return { name: productName, removed };
  const cleaned = tidy(name);
  if (!cleaned) return { name: productName, removed: [] };
  return { name: cleaned, removed };
}

/**
 * 사용자가 상품 특징에 «… 표기 금지»처럼 막은 표현을 뽑는다(PR261016-6E43DF «인증되지 않은 자외선 차단율·보호구 효능 표기 금지»).
 * 꼬리 낱말(율·효능·표기 …)을 뗀 줄기도 함께 막는다 — «자외선 차단율» 금지면 «자외선 차단»도 금지.
 * @param {string} productFeature
 * @returns {string[]} 막을 표현(긴 것부터)
 */
// «제거·삭제·없애» 는 표현·문구·표기 같은 말이 바로 앞일 때만(E9820A «… 표현 제거») — «먼지 제거 기능»·«삭제 버튼» 같은 기능·사용법 문장은 금지가 아니다
const BAN_SENTENCE_RE = /^(.*?)\s*(?:(?:표기|표시|표현|문구|사용|언급)?\s*(?:금지|하지\s*말|쓰지\s*말|넣지\s*말|빼\s*주|제외)|(?:표기|표시|표현|문구|언급|단어|명칭)\s*(?:제거|삭제|없애))/;
// 부드러운 꼴 «사용하지 않아야/않도록/마세요»·«사용 불가/X/안 됨»(PR261017-E63D88) — «세탁기 사용하지 않아야»(사용법)와 가르려고
// 대상이 따옴표로 감싸였거나 문장에 «단어·표현·문구·표기·명칭·말» 이 있을 때만 금지로 본다
const BAN_SOFT_RE = /^(.*?)\s*(?:(?:표기|표시|표현|문구|사용|언급)?\s*(?:하|쓰|넣)지\s*(?:않아야|않도록|마세요)|(?:표기|표시|표현|문구|사용|언급)\s*(?:불가|[Xx](?![A-Za-z0-9])|(?:하면\s*)?안\s*(?:됨|돼|된다)))/;
const BAN_SOFT_CUE_RE = /['"«»“”‘’]|단어|표현|문구|표기|명칭|(?:^|[\s'"»”’])말(?:은|는|을|를|도)?(?=\s|$)/;
function matchBan(sentence) {
  return sentence.match(BAN_SENTENCE_RE) || (BAN_SOFT_CUE_RE.test(sentence) ? sentence.match(BAN_SOFT_RE) : null);
}
// «'피그먼트'라는 단어는» → 피그먼트
const BAN_WORD_LABEL_RE = /(?:이)?라는\s*(?:단어|표현|말|문구|용어)(?:은|는|을|를|도)?|\s*(?:단어|표현|용어)(?:은|는|을|를|도)?$/g;
// «X로 단정·표시» 꼴 — 조사·동사를 떼고 낱말마다 막는다(«어린이 완구로» → 어린이·완구). 소재·재질 같은 말은 막지 않는다
const BAN_AS_RE = /\s*(?:으로|로)\s*(?:단정|표시|표기|기재|광고|소개)?$/;
const BAN_WORD_SKIP = /^(?:소재|재질|원단|제품|상품|용품)$/;

export function bannedPhrasesFromFeature(productFeature, targetKeyword) {
  const out = new Set();
  // 줄표(—)도 문장 경계 — «모조 섬유 — 천연 앙고라·천연 밍크로 표현 금지» 의 앞말은 금지 대상이 아니다(F84B02)
  String(productFeature || '').split(/[.。\n!;—–]+/).forEach(sentence => {
    negatedNouns(sentence).forEach(w => out.add(w));
    // 한 문장에 금지가 여럿이면(«… 표현 금지, 혼용률·인증 수치 추정 금지» · «… 옵션 제외, … 효능 금지») 남은 말도 차례로 본다(F84B02)
    let rest = sentence;
    for (let m = matchBan(rest); m; m = matchBan(rest)) {
      rest = rest.slice(m.index + m[0].length).replace(/^[\s,]+/, '');
      if (!m[1].trim()) continue;
      const body = m[1].replace(/^.*(?:않은|없는|아닌)\s*/, '');
      // «실리콘 소재로 단정하거나 어린이 완구로» 처럼 «~거나» 로 이은 것도 나눈다(PR261017-B7DD35)
      const pieces = body.split(/[·,/、]|\s및\s|\s또는\s|(?:하|이)?거나\s+/)
        .map(raw => raw.replace(/[«»"'“”‘’()]/g, '').trim().replace(BAN_WORD_LABEL_RE, '').replace(/(\S{2})(?:은|는|을|를)$/, '$1').trim());
      // «천연 앙고라·천연 밍크로 표현» — 끝의 «~로» 는 나열 전부에 걸린다(F84B02: 앙고라만 낱말로 안 막혀 상품명에 남음)
      const asAll = pieces.length > 1 && BAN_AS_RE.test(pieces[pieces.length - 1]);
      pieces.forEach(piece => {
        let p = piece;
        const asForm = asAll || BAN_AS_RE.test(p);
        if (BAN_AS_RE.test(p)) p = p.replace(BAN_AS_RE, '').trim();
        if (p.length < 2) return;
        out.add(p);
        const stem = p.replace(/\s*(?:율|률|효능|효과|기능|표기|표현|문구|수치)$/, '').trim();
        if (stem.length >= 2) out.add(stem);
        if (asForm) p.split(/\s+/).forEach(w => { if (w.length >= 2 && !BAN_WORD_SKIP.test(w)) out.add(w); });
      });
    }
  });
  // 막힌 말과 겹치는 대상 키워드(«실리콘타프» 아님 ↔ 키워드 «실타프»)도 막는다 — 상품명에 키워드를 억지로 넣지 않게(PR261018-3DBE82)
  const kw = String(targetKeyword || '').replace(/\s+/g, '');
  if (kw.length >= 2 && [...out].some(p => keywordOverlapsBan(kw, p.replace(/\s+/g, '')))) out.add(kw);
  return [...out].sort((a, b) => b.length - a.length);
}

// «X 아님 / X 아닙니다 / X 아니에요 / X가 아닌» — X 는 바로 앞 낱말 하나만. «식품용 아님»·«해당 아님» 같은 일반 문장은 뺀다
const NEG_RE = /(?:^|[\s,·])['"«“‘]?([0-9A-Za-z가-힣]{2,12})['"»”’]?\s*(?:아님|아닙니다|아니에요|아니예요|아닌)(?=$|[\s,.)])/g;
const NEG_SKIP = /(?:용|것|거|게|건|뿐|때문|정도|사실|문제|해당|대상|필수|무료|전부|모두|그냥)$/;
function negatedNouns(sentence) {
  const out = [];
  let m;
  NEG_RE.lastIndex = 0;
  while ((m = NEG_RE.exec(sentence))) {
    let w = m[1];
    if (/가$/.test(w) && w.length > 2) w = w.slice(0, -1);
    else if (/이$/.test(w) && w.length >= 4) w = w.slice(0, -1);
    if (w.length >= 2 && !NEG_SKIP.test(w)) out.push(w);
  }
  return out;
}

/** 키워드와 막힌 말이 같거나 서로 들어 있거나, 첫·끝 글자가 같고 키워드 글자가 차례대로 들어 있으면(실타프 ⊂ 실리콘타프) 겹친다 */
function keywordOverlapsBan(kw, p) {
  if (p.length < 2) return false;
  if (kw.includes(p) || p.includes(kw)) return true;
  if (kw[0] !== p[0] || kw[kw.length - 1] !== p[p.length - 1]) return false;
  let i = 0;
  for (const ch of p) if (ch === kw[i]) i++;
  return i === kw.length;
}

/** 상품 특징에서 «… 금지»·«…하지 말 것» 문장을 뺀 글 — 금지 문장 속 낱말(실리콘·어린이)을 근거나 대상으로 읽지 않게 */
export function featureWithoutBans(productFeature) {
  return String(productFeature || '').split(/[.。\n!;]+/).filter(s => !matchBan(s)).join('. ');
}

// 사용자가 «원산지» 를 막았으면 낱말만이 아니라 원산지 주장(나라 이름·«…산»)도 막는다(jaemaro C5DE24 «확인되지 않은 원산지 금지» 인데 澳洲 → «100% 호주 울»).
// 근거 = 1688 원산지 속성 값이 그 나라일 때만. 붙은 꾸밈말(일본풍·미국식)은 원산지가 아니다
const ORIGIN_BAN_RE = /원산지|생산지|제조국/;   // 맨 «산지» 는 «등산지팡이» 같은 말에도 걸려 뺀다
const ORIGIN_ATTR_RE = /원산지|산지|생산지|제조국|产地|原产地|产区|origin/i;
const FOREIGN_ORIGINS = [
  ['호주|오스트레일리아', '澳洲|澳大利亚|australia'], ['뉴질랜드', '新西兰|new\\s*zealand'], ['미국', '美国|usa|america'], ['일본', '日本|japan'],
  ['독일', '德国|germany'], ['이탈리아', '意大利|italy'], ['프랑스', '法国|france'], ['영국', '英国|britain|england|uk'], ['캐나다', '加拿大|canada'],
  ['스페인', '西班牙|spain'], ['스위스', '瑞士|switzerland'], ['벨기에', '比利时|belgium'], ['덴마크', '丹麦|denmark'], ['북미', '北美|north\\s*america'], ['유럽', '欧洲|europe']
];

function dropForeignOrigins(name, attributes, removed) {
  const basis = (Array.isArray(attributes) ? attributes : []).filter(a => a && ORIGIN_ATTR_RE.test(`${a.attributeName || ''} ${a.attributeNameTrans || ''}`))
    .map(a => `${a.value || ''} ${a.valueTrans || ''}`).join(' ');
  let out = name;
  FOREIGN_ORIGINS.forEach(([ko, src]) => {
    if (new RegExp(`${ko}|${src}`, 'i').test(basis)) return;
    out = out.replace(new RegExp(`(?<![가-힣A-Za-z])(?:${ko})산?(?=\\s|$|[,/·)])`, 'g'), (m) => { removed.push(m); return ' '; });
  });
  return out;
}

/**
 * @param {string} productName
 * @param {string} productFeature
 * @param {Array} [attributes] - 1688 속성(원산지를 막았을 때 그 나라가 원산지 값이면 남긴다)
 * @param {string} [targetKeyword] - 막힌 말과 겹치면 키워드도 뺀다
 * @param {{ allowEmpty?: boolean }} [opts] - allowEmpty: 다 빠지면 빈 이름(되살림 0 · 부른 쪽이 크게 실패)
 * @returns {{ name: string, removed: string[] }}
 */
export function dropBannedFeatureWords(productName, productFeature, attributes, targetKeyword, opts) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  const banned = bannedPhrasesFromFeature(productFeature, targetKeyword);
  if (!banned.length) return { name: productName, removed: [] };
  const removed = [];
  let name = productName;
  banned.forEach(p => {
    const re = new RegExp(p.split(/\s+/).map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s*'), 'g');
    name = name.replace(re, (m) => { removed.push(m); return ' '; });
  });
  if (banned.some(p => ORIGIN_BAN_RE.test(p))) name = dropForeignOrigins(name, attributes, removed);
  if (!removed.length) return { name: productName, removed };
  const cleaned = tidy(name);
  if (!cleaned && !(opts && opts.allowEmpty)) return { name: productName, removed: [] };
  return { name: cleaned, removed };
}

/**
 * 소재 묶음 한 곳 — 상세이미지 문구 검사(detailClaimGuard)와 상품명 검사(UNSUPPORTED_RULES)가 같이 쓴다(61CACC: 두 목록이 갈려 상품명 쪽에 나일론·쿨론이 없었다).
 * re = 소재 값·근거 글에서 묶음을 알아보는 꼴(같은 묶음이면 같은 소재) · claim = 상품명·문구 속 소재 주장(있는 묶음만 근거 없으면 뺀다) · src = 주장의 근거 꼴(없으면 re)
 */
export const MATERIALS = [
  // 涤 = 涤纶 줄임(涤棉 = 폴리에스터+면 · 61CACC)
  { key: 'polyester', re: /폴리\s*에스(?:터|테르)|폴리(?![가-힣]*우레탄)|涤|聚酯|polyester/i, claim: /폴리\s*에스(?:터|테르)/g },
  // 홀로 쓴 «면»«마»는 소재 값 자체(«면», «면·마», «린넨/면»)일 때만 — 값 맨 앞이거나 «·»«/»로 이어진 칸.
  // «앞 면 인쇄»(쪽), «사이즈 1, 마», «(면)» 은 소재가 아니다. «면 100%» 처럼 함량이 붙은 면만 더 인정, 마는 함량만으로는 안 본다
  { key: 'cotton', re: /순면|코튼|(?<=(?:^|[·/])\s*)면(?=\s*(?:$|[·/]|\d+(?:\.\d+)?\s*%))|棉|cotton/i, claim: /순면|코튼|(?<![가-힣])면(?=\s*(?:혼방|폴리|스판|나일론|레이온))/g, src: /棉|cotton|면|코튼/i },
  { key: 'linen', re: /린넨|리넨|(?<=(?:^|[·/])\s*)마(?=\s*(?:$|[·/]))|麻|linen/i, claim: /린넨/g, src: /麻|linen|린넨/i },
  // 아이스 실크(冰丝)는 원단 이름, 실크 인쇄(丝印)는 인쇄 방식 — 실크 소재 주장이 아니다
  { key: 'silk', re: /(?<!아이스\s*)실크|真丝|桑蚕丝|silk/i, claim: /(?<!아이스\s*)실크(?!\s*(?:인쇄|스크린|프린트|프린팅))/g, src: /丝|silk|실크/i },
  // PU 는 낱말로만 — «Spun»·«PUMA» 안의 pu 는 아니다
  { key: 'pu', re: /(?<![A-Za-z])PU(?![A-Za-z])|인조\s*가죽|합성\s*가죽|폴리우레탄/i },
  // PU·인조 가죽은 가죽 주장이 아니고, 가죽 질감·무늬는 겉모양이다
  { key: 'leather', re: /(?<!(?:PU|인조|합성)\s*)(?:나파\s*)?가죽|레더|真皮|牛皮|纳帕|leather/i, claim: /(?<!(?:PU|인조|합성)\s*)(?:천연\s*가죽|소가죽|나파\s*가죽|나파|가죽|레더)(?!\s*(?:질감|무늬|패턴|텍스처|느낌))/g, src: /皮|leather|가죽|레더|나파/i },
  { key: 'nylon', re: /나일론|锦纶|尼龙|nylon/i, claim: /나일론/g },
  { key: 'wool', re: /양모|羊毛|wool|(?<![가-힣])울(?![가-힣])/i, claim: /양모|(?<![가-힣])울(?=\s|$)/g, src: /羊毛|wool|양모|울/i },
  { key: 'spandex', re: /스판|spandex|氨纶/i, claim: /스판(?:덱스)?/g },
  // 섬유 상표형 낱말 — 1688 속성이 폴리에스터여도 AI 가 «쿨론» 을 붙인다(61CACC)
  { key: 'coolon', re: /쿨론|coolon/i, claim: /쿨론/g },
  { key: 'coolmax', re: /쿨맥스|coolmax/i, claim: /쿨맥스/g },
  { key: 'tencel', re: /텐셀|tencel|天丝/i, claim: /텐셀/g },
  { key: 'modal', re: /모달|modal|莫代尔/i, claim: /모달/g },
  { key: 'lycra', re: /라이크라|lycra|莱卡/i, claim: /라이크라/g },
  // 털·기모 — 仿TU绒(인조 벨벳)을 «밍크/밍크기모»로, 狼毫(족제비 붓털)를 «늑대털»로 옮긴다(D176A0 · 293924 · AFE4F0). 仿·人造·인조 앞붙은 원문은 천연 근거가 아니다
  { key: 'mink', re: /(?<!仿|人造|人工)貂|(?<!faux\s*)mink|(?<!인조\s*|페이크\s*)밍크/i, claim: /(?<!인조\s*|페이크\s*)밍크/g },
  { key: 'fur', re: /(?<!仿|人造|人工)(?:皮草|真毛|狼毛|兔毛|狐狸毛|貉子毛)|real\s*fur|천연\s*(?:모피|털)/i, claim: /(?<!인조\s*|페이크\s*)(?:천연\s*)?(?:모피|리얼\s*퍼|(?:늑대|토끼|여우|라쿤|족제비)\s*털)/g },
  { key: 'fleece', re: /기모|加绒|抓绒|磨毛|fleece/i, claim: /기모/g, src: /绒|磨毛|fleece|기모/i },
  // 광물·보석·귀금속·합성수지 — 玉石→«운모»·PET→«아크릴»·合金→«다이아몬드·화이트K»(76CB52 · 5B4360 · 06A6B3 · cpl-505a-seomaterial-2). 水钻·人造钻石 은 다이아몬드 근거 아님
  { key: 'mica', re: /운모|云母|mica/i, claim: /운모/g },
  { key: 'acrylic', re: /아크릴|亚克力|压克力|有机玻璃|(?<!聚)丙烯|acrylic|PMMA/i, claim: /아크릴/g },
  { key: 'diamond', re: /(?<!水|人造|仿|合成|人工)钻石|diamond(?!\s*cut)|천연\s*다이아/i, claim: /(?:천연\s*|리얼\s*)?다이아몬드(?!\s*(?:컷|커팅|패턴|무늬|모양|형))/g },
  { key: 'gold', re: /(?<![A-Za-z0-9])(?:9|10|14|18|22|24)\s*K(?![A-Za-z])|K金|黄金|足金|纯金/i, claim: /(?<![A-Za-z0-9])(?:(?:9|10|14|18|22|24)\s*K|(?:화이트|로즈|옐로우|옐로|핑크)\s*K)(?![A-Za-z])(?:\s*골드)?|순금/g },
  { key: 'cashmere', re: /캐시미어|羊绒|cashmere/i, claim: /캐시미어/g },
  { key: 'eva', re: /EVA/i },
  { key: 'abs', re: /ABS/i },
  // 플라스틱·고무 계열(PR261017-B7DD35: 재질 PE·TPE 인데 «실리콘타이») — 영문 약자는 앞뒤가 영문자면 다른 말(TPE 의 PE, PET)
  { key: 'pp', re: /(?<![A-Za-z])PP(?![A-Za-z])|폴리프로필렌/i, claim: /(?<![A-Za-z])PP(?![A-Za-z])/g, src: /(?<![A-Za-z])PP(?![A-Za-z])|聚丙烯|polypropylene|폴리프로필렌/i },
  { key: 'tpe', re: /(?<![A-Za-z])TPE(?![A-Za-z])|热塑性弹性体/i, claim: /(?<![A-Za-z])TPE(?![A-Za-z])/g },
  { key: 'pe', re: /(?<![A-Za-z])PE(?![A-Za-z])|聚乙烯|polyethylene|폴리에틸렌/i, claim: /(?<![A-Za-z])PE(?![A-Za-z])/g },
  { key: 'tpr', re: /TPR/i },
  { key: 'silicone', re: /실리콘|硅胶|silicone/i, claim: /실리콘/g, src: /硅胶|硅|silicone|실리콘/i },
  { key: 'steel', re: /스테인리스|스틸|강철|钢|steel/i }
];

const MATERIAL_ATTR = /재질|소재|원단\s*성분|직물\s*성분|원자재|材质|面料|成分|material|fabric/i;
// 쉼표 뒤 칸이 홀로 쓴 «면»«마»(함량 붙어도)뿐일 때 — 앞 칸이 소재일 때만 소재 목록으로 본다
const LONE_AFTER_COMMA = [['cotton', /^면(?:\s*\d+(?:\.\d+)?\s*%)?$/], ['linen', /^마(?:\s*\d+(?:\.\d+)?\s*%)?$/]];

/** 글 속 소재 묶음 key 목록 */
export function familiesOf(text) {
  const t = String(text || '');
  const found = MATERIALS.filter(m => m.re.test(t)).map(m => m.key);
  // «폴리, 면» 처럼 소재를 쉼표로 늘어놓은 값 — «사이즈 1, 마» 는 앞 칸이 소재가 아니라 안 본다
  let prevIsMaterial = false;
  t.split(/[,，]/).forEach((part, i) => {
    const seg = part.trim();
    const lone = LONE_AFTER_COMMA.find(([, re]) => re.test(seg));
    if (i > 0 && prevIsMaterial && lone && !found.includes(lone[0])) found.push(lone[0]);
    if (!lone) prevIsMaterial = MATERIALS.some(m => m.re.test(seg));   // 홀로 쓴 면·마 칸은 앞 칸 판단을 이어 간다(«폴리, 면, 마»)
  });
  return found;
}

/** 1688 소재 속성 값 전부(함량 행 포함) — 소재 묶음 셈용 */
function materialAttrValues(attributes) {
  return (Array.isArray(attributes) ? attributes : [])
    .filter(a => a && MATERIAL_ATTR.test(`${a.attributeName || ''} ${a.attributeNameTrans || ''}`))
    .map(a => `${a.value || ''} ${a.valueTrans || ''}`)
    .join('/');
}

/** 1688 속성의 소재 값(함량 행 제외, 괄호 설명 제외) */
export function attributeMaterial(attributes) {
  const vals = (Array.isArray(attributes) ? attributes : [])
    .filter(a => a && MATERIAL_ATTR.test(`${a.attributeName || ''} ${a.attributeNameTrans || ''}`) && !/함량|含量/.test(a.attributeName || ''))
    .map(a => String(a.valueTrans || a.value || '').replace(/\s*[(（][^()（）]*[)）]/g, '').trim())
    .filter(Boolean);
  return [...new Set(vals)].join('/');
}

/**
 * 근거 없는 소재·효능 — 1688 원 제목의 광고 문구(真丝·防晒·冰丝·一键)를 AI 가 상품명에 옮긴다(PR261016-9A99EE).
 * 근거 = 1688 속성 값·남긴 옵션 원문/영문·상품 특징·대상 키워드. 원 제목은 근거가 아니다.
 * 대상 키워드 안의 낱말(사용자가 고른 «면토시» 등)은 건드리지 않는다.
 */
const UNSUPPORTED_RULES = [
  // 효능
  { kind: 'claim', re: /자외선\s*차단|UV\s*차단|썬\s*블록/gi, src: /防晒|防紫外|紫外线|UV|자외선|sun\s*protect/i },
  { kind: 'claim', re: /쿨링|냉감/g, src: /冰丝|冰感|凉感|凉爽|cool|ice|쿨링|냉감|아이스/i },
  { kind: 'claim', re: /(?:1초\s*)?(?:원터치\s*)?자동\s*털\s*제거|원터치\s*자동/g, src: /自动|automatic|자동/i },
  { kind: 'claim', re: /항균/g, src: /抗菌|antibacterial|항균/i },
  { kind: 'claim', re: /방수/g, src: /防水|waterproof|방수/i },
  // 소재 — MATERIALS 한 곳(claim 있는 묶음)
  ...MATERIALS.filter(m => m.claim).map(m => ({ kind: 'material', re: m.claim, src: m.src || m.re })),
  // «혼방» 은 1688 속성 소재가 둘 이상일 때만(61CACC: 폴리에스터 하나인데 «쿨론 혼방») — 함량 행(材质含量 涤纶65%棉35%)도 센다
  { kind: 'material', re: /혼방/g, ok: s => familiesOf(materialAttrValues(s.attributes)).length >= 2 },
  // 안전·인증 주장 — 1688 속성·인증 칸·상품 특징에 근거가 있을 때만 남긴다
  { kind: 'safety', re: /무독성|무독|무해/g, src: /无毒|无害|non-?toxic|무독|무해/i },
  { kind: 'safety', re: /KC\s*(?:인증|안전\s*인증)?/g, src: /(?<![A-Za-z])KC(?![A-Za-z])/i },
  { kind: 'safety', re: /환경\s*호르몬\s*(?:무검출|불검출|미검출|없는|free|프리)?|BPA\s*(?:프리|free|무검출|불검출)/gi, src: /BPA|双酚|环境激素|환경\s*호르몬/i }
];

/**
 * @param {string} productName
 * @param {{ attributes?: Array, options?: Array, productFeature?: string, targetKeyword?: string }} src
 * @param {{ kinds?: string[], keepPercent?: boolean, allowEmpty?: boolean }} [opts] - kinds: 볼 규칙 종류(기본 전부) · keepPercent: 함량 괄호를 남긴다(상세이미지 문구용) · allowEmpty: 다 빠져도 빈 값으로 돌려준다(검색태그용)
 * @returns {{ name: string, removed: string[] }}
 */
export function dropUnsupportedClaims(productName, src, opts) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  const s = src || {};
  // 상품 특징의 «… 금지» 문장 속 낱말(«실리콘 소재로 단정하지 말 것»)은 근거가 아니다
  const basisNoKw = [
    ...(Array.isArray(s.attributes) ? s.attributes : []).filter(Boolean).map(a => `${a.value || ''} ${a.valueTrans || ''}`),
    ...(Array.isArray(s.options) ? s.options : []).filter(Boolean)
      .map(o => ['option1', 'option2'].map(k => (o[k] ? `${o[k].name || ''} ${o[k].nameTrans || ''}` : '')).join(' ')),
    featureWithoutBans(s.productFeature)
  ].join(' \n ');
  const basis = `${basisNoKw} \n ${String(s.targetKeyword || '')}`;
  const kw = String(s.targetKeyword || '').replace(/\s+/g, '');
  const removed = [];
  const kept = [];   // 대상 키워드 안이라 남긴 소재 낱말인데 1688 속성·특징엔 근거가 없는 것 — 지우지 않고 경고만
  let name = productName;
  const o = opts || {};
  UNSUPPORTED_RULES.forEach(r => {
    if (Array.isArray(o.kinds) && !o.kinds.includes(r.kind)) return;
    if (r.ok ? r.ok(s) : r.src.test(basis)) {
      if (r.kind === 'material' && r.src && !r.src.test(basisNoKw)) (productName.match(r.re) || []).forEach(m => kept.push(m));
      return;
    }
    name = name.replace(r.re, (m) => {
      if (kw && kw.includes(m.replace(/\s+/g, ''))) { if (r.kind === 'material') kept.push(m); return m; }
      removed.push(m);
      return ' ';
    });
  });
  // 성분 함량 괄호 «(폴리에스터 96% 이상)» — 상품명에 넣지 않는다
  if (!o.keepPercent) name = name.replace(/\s*[(（][^()（）]*\d+(?:\.\d+)?\s*%[^()（）]*[)）]/g, (m) => { removed.push(m.trim()); return ' '; });
  if (!removed.length) return { name: productName, removed, kept };
  const cleaned = tidy(name);
  if (!cleaned && !o.allowEmpty) return { name: productName, removed: [], kept };
  return { name: cleaned, removed, kept };
}

/**
 * 검색태그 사실성 — 상품명과 같은 검사를 태그 하나씩에 건다(PR261017-B7DD35 · 48349F: «어린이용품·무독성·KC인증·실리콘» 태그).
 * 걸리면 태그를 통째로 뺀다: 상품 특징의 금지 표현 · 근거 없는 소재·효능·안전 주장 · 특징과 다른 대상 · 어린이용 아닌 카테고리의 어린이 낱말.
 * @param {string[]} keywords
 * @param {{ attributes?: Array, options?: Array, productFeature?: string, targetKeyword?: string, categoryPath?: string }} src
 * @returns {{ keywords: string[], removed: string[] }}
 */
export function dropUnsupportedTags(keywords, src) {
  if (!Array.isArray(keywords)) return { keywords, removed: [] };
  const s = src || {};
  const banned = bannedPhrasesFromFeature(s.productFeature, s.targetKeyword).map(p => p.replace(/\s+/g, ''));
  const feat = `${featureWithoutBans(s.productFeature)} ${String(s.targetKeyword || '')}`;
  const adult = FEATURE_ADULT_RE.test(feat);
  const kids = FEATURE_KIDS_RE.test(feat);
  const otherSide = adult === kids ? null : new RegExp(`${KIDS_PRE}(${adult ? `${KIDS_WORD}|${BABY_WORD}${KIDS_END}` : ADULT_WORD})`, 'i');
  const removed = [];
  const out = keywords.filter(k => {
    const t = String(k || '');
    const flat = t.replace(/\s+/g, '');
    const bad = banned.some(p => flat.includes(p))
      || dropUnsupportedClaims(t, s, { allowEmpty: true }).removed.length > 0
      || (!!otherSide && otherSide.test(t))
      || stripKidsWords(t, s.categoryPath).removed.length > 0;
    if (bad) removed.push(t);
    return !bad;
  });
  return { keywords: out, removed };
}

/**
 * 옵션명 낱말 표 — 원문 낱말 → 한글 한 곳(PR261021-3D32BB: AI 가 长筒/中筒 을 옵션끼리 뒤바꾸고 毛巾底 를 «기모» 로 옮김).
 * 장목·중목 = 고객 보고 낱말 · 늘릴 땐 여기만.
 */
const OPTION_TERMS = [
  { src: /长筒/, ko: '장목' },
  { src: /中筒/, ko: '중목' },
  { src: /短筒/, ko: '단목' },
  { src: /毛巾底/, ko: '타월 바닥' },
  { src: /加绒|抓绒|fleece/i, ko: '기모' }
];

// 1688 색상 원문 → 쿠팡 색상 이름(긴 것부터 맞춘다) · 옵션명 색 대조와 노출속성 겹침 정리(splitOptionGroups)가 같이 쓴다 — 늘릴 땐 여기만.
export const CN_COLOR_KO = {
  '军绿': '아미그린', '橄榄绿': '올리브그린', '墨绿': '다크그린', '深绿': '다크그린', '草绿': '그라스그린',
  '荧光绿': '형광그린', '薄荷绿': '민트', '牛油果绿': '아보카도그린', '浅绿': '라이트그린', '绿': '그린',
  '卡其': '카키', '驼': '카멜', '焦糖': '카라멜', '咖啡': '커피', '棕': '브라운', '米白': '아이보리',
  '米': '베이지', '杏': '살구', '乳白': '밀키화이트', '奶白': '밀키화이트', '白': '화이트', '黑': '블랙',
  '浅灰': '라이트그레이', '深灰': '다크그레이', '烟灰': '스모크그레이', '炭灰': '차콜', '灰': '그레이',
  '藏青': '네이비', '藏蓝': '네이비', '宝蓝': '로얄블루', '湖蓝': '레이크블루', '天蓝': '스카이블루',
  '孔雀蓝': '피콕블루', '雾霾蓝': '더스티블루', '浅蓝': '라이트블루', '深蓝': '다크블루', '蓝': '블루',
  '酒红': '버건디', '枣红': '와인', '玫红': '로즈레드', '西瓜红': '워터멜론레드', '红': '레드',
  '粉红': '핑크', '藕粉': '더스티핑크', '粉': '핑크', '浅紫': '라이트퍼플', '香芋紫': '라벤더', '紫': '퍼플',
  '姜黄': '진저옐로우', '黄': '옐로우', '橙': '오렌지', '金': '골드', '银': '실버'
};
const CN_KEYS = Object.keys(CN_COLOR_KO).sort((a, b) => b.length - a.length);
const COLOR_KO_WORDS = new Set(Object.values(CN_COLOR_KO));
// 사전이 모르는 색 표기 — 이게 있으면 원문 색을 모르는 것이라 옵션명 색을 건드리지 않는다
const UNKNOWN_COLOR_RE = /色|colou?r|black|white|red|blue|green|yellow|purple|pink|gr[ae]y|brown|beige|khaki|navy|ivory|mint|silver|gold|orange/i;

/**
 * 원문 속 서로 다른 색 낱말 수(가장 긴 일치부터 · 金属·숫자 뒤는 색 아님).
 * 둘 이상이면 어느 것이 그 옵션 색인지 모른다 — 银扣黑带(은색 버클·검은 띠)를 앞글자로 «실버» 라 하면 안 된다(보고 3320E1)
 */
function colorWordCount(s) {
  const seen = new Set();
  for (let i = 0; i < s.length;) {
    const key = surelyNotColorAt(s, i) ? null : CN_KEYS.find(k => s.startsWith(k, i));
    if (key) { seen.add(CN_COLOR_KO[key]); i += key.length; } else i++;
  }
  return seen.size;
}

/** 1688 색상 원문 → 쿠팡 색상 이름(모르면 '' · 색 낱말이 둘 이상이어도 '') */
export function cnColorToKo(cn) {
  if (colorWordCount(String(cn || '')) > 1) return '';
  const s = String(cn || '').replace(/[色款（(].*$/, '').trim() || String(cn || '');
  for (const k of CN_KEYS) {
    if (s === k || s.startsWith(k)) return CN_COLOR_KO[k];
  }
  return '';
}

/**
 * 원문 글 속 색 이름들 — 사전 가장 긴 일치를 잇달아 붙은 만큼 한 색으로(浅蓝+紫 → 라이트블루퍼플).
 * 한 글자 키는 色·款·끝·구분자(한자 아닌 글자)·다른 색 앞일 때만 색이다 — 粉末(가루)·紫外线(자외선)·米奇(미키)·金属(금속)은 색 아님.
 * 숫자 바로 뒤 한 글자(1米 = 1미터)도 색이 아니다.
 * 그 밖에 한 글자 키 뒤가 다른 한자면(白条纹·红底·粉末) 색인지 모른다 → unsure.
 */
function colorKeyAt(s, i) {
  return CN_KEYS.find(key => s.startsWith(key, i) && (key.length > 1 || singleColorAt(s, i)));
}
function singleColorAt(s, i) {
  if (/\d/.test(s[i - 1] || '')) return false;
  const next = s[i + 1];
  return !next || /[色款]|[^一-鿿]/.test(next) || !!colorKeyAt(s, i + 1);
}
const surelyNotColorAt = (s, i) => /\d/.test(s[i - 1] || '') || (/[金银]/.test(s[i]) && /[属屬]/.test(s[i + 1] || ''));
function scanColors(text) {
  const s = String(text || '');
  const out = [];
  let run = '';
  let unsure = false;
  for (let i = 0; i < s.length;) {
    const k = colorKeyAt(s, i);
    if (k) { run += CN_COLOR_KO[k]; i += k.length; continue; }
    if (CN_COLOR_KO[s[i]] && !surelyNotColorAt(s, i)) unsure = true;
    if (run) { out.push(run); run = ''; }
    i++;
  }
  if (run) out.push(run);
  return { colors: [...new Set(out)], unsure };
}
export function cnColorsKo(text) {
  return scanColors(text).colors;
}

// «X色» 인데 X 가 사전 색이 아님(咖色·蓝灰色 의 끝 글자 아닌 것) — 색이 아닌 말(颜色·纯色·同色·多色·撞色 …)은 뺀다
const NOT_COLOR_BEFORE_SE = /[颜纯同混多单双撞杂彩本原拼变异各三四五六七八九十]/;
function hasUnlistedColor(cn) {
  const s = String(cn || '');
  for (let i = 1; i < s.length; i++) {
    if (s[i] !== '色') continue;
    const x = s[i - 1];
    if (!/[一-鿿]/.test(x) || NOT_COLOR_BEFORE_SE.test(x)) continue;
    if (!CN_KEYS.some(k => s.slice(0, i).endsWith(k))) return true;
  }
  return false;
}

// 옵션명의 색 낱말이 그 옵션 원문 색에 없으면 원문 색으로 바꾸고, 바꿀 원문 색이 없으면 뺀다(PR261027-DB248D: 浅蓝紫 → «민트»).
function fixOptionColor(name, s) {
  const cn = ['option1', 'option2'].map(k => (s[k] && s[k].name) || '').join(' ');
  const trans = ['option1', 'option2'].map(k => (s[k] && s[k].nameTrans) || '').join(' ');
  // 원문 색 = 한자 색(사전) + 원문에 한글로 적힌 색(블랙)
  const scan = scanColors(cn);
  if (scan.unsure) return name; // 원문 색을 모른다 — 넣지도 빼지도 않는다
  // 사전 밖 «X色»(咖色 = 갈색)도 원문 색을 모르는 것 — 银色针扣咖色 을 [실버] 로만 읽고 «브라운» 을 «실버» 로 바꿨다(보고 3320E1)
  if (hasUnlistedColor(cn)) return name;
  const src = [...new Set([...scan.colors, ...[...COLOR_KO_WORDS].filter(w => cn.includes(w))])];
  if (!src.length && UNKNOWN_COLOR_RE.test(`${cn} ${trans}`)) return name;
  const tokens = name.split(/\s+/);
  const bare = t => t.replace(/색$/, '');
  const wrong = tokens.filter(t => COLOR_KO_WORDS.has(bare(t)) && !src.some(c => c.includes(bare(t))));
  if (!wrong.length) return name;
  // 바꿀 원문 색 = 틀린 낱말 말고 남은 낱말에 없는 것(다크그레이 속 «그레이» 는 있다고 치지 않는다)
  const missing = src.filter(c => !tokens.some(t => !wrong.includes(t) && t.includes(c)));
  return tokens.map(t => (wrong.includes(t) ? (missing.shift() || '') : t)).join(' ');
}

/**
 * SEO 옵션명(korName)을 그 옵션 제 원문(option1/2.name·nameTrans)과 대조한다 — 원문 근거 없는 표 낱말·색 낱말은 원문에 있는 것으로 바꾸고, 바꿀 것이 없으면 뺀다.
 * 원문 옵션을 optionNumber 로 못 찾으면 손대지 않는다.
 * @param {Array<{optionNumber:string, korName:string}>} options
 * @param {Array} sourceOptions - 상품 옵션(원문)
 * @returns {{ options: Array, changed: Array<{optionNumber:string, from:string, to:string}> }}
 */
export function fixOptionTerms(options, sourceOptions) {
  if (!Array.isArray(options) || !Array.isArray(sourceOptions)) return { options, changed: [] };
  const changed = [];
  const out = options.map(o => {
    const s = o && sourceOptions.find(x => x && x.optionNumber === o.optionNumber);
    if (!s || typeof o.korName !== 'string') return o;
    const basis = ['option1', 'option2'].map(k => (s[k] ? `${s[k].name || ''} ${s[k].nameTrans || ''}` : '')).join(' ');
    const has = OPTION_TERMS.filter(r => r.src.test(basis));
    const missing = has.filter(r => !o.korName.includes(r.ko));
    let name = o.korName;
    OPTION_TERMS.filter(r => !has.includes(r) && name.includes(r.ko)).forEach(r => {
      const to = missing.shift();
      name = name.split(r.ko).join(to ? to.ko : ' ');
    });
    name = fixOptionColor(name, s);
    if (name === o.korName) return o;
    const cleaned = tidy(name);
    changed.push({ optionNumber: o.optionNumber, from: o.korName, to: cleaned });
    return { ...o, korName: cleaned };
  });
  return { options: out, changed };
}

/**
 * SEO 옵션명(korName)에 상품명과 같은 사실 검사 — 근거 없는 소재·효능·인증 주장과 상품 특징에서 금지한 표현을 뺀다(D176A0: 仿TU绒 인데 옵션 9개 «밍크기모»).
 * 근거 = 1688 속성 · 그 옵션 제 원문 · 상품 특징(금지 문장 뺀 글). 대상 키워드는 옵션의 근거가 아니다(상품명 예외만 #1982 그대로).
 * 원문 옵션을 optionNumber 로 못 찾으면 손대지 않는다 · 다 빠지면 그 옵션 원문 번역을 같은 검사에 한 번 더 · 그래도 비면 empty 에 담는다(원래 이름 되살림 0 · 부른 쪽이 크게 실패).
 * @param {Array<{optionNumber:string, korName:string}>} options
 * @param {{ attributes?: Array, options?: Array, productFeature?: string }} src - 상품(원문 옵션 = src.options)
 * @returns {{ options: Array, changed: Array<{optionNumber:string, from:string, to:string}>, empty: Array<{optionNumber:string, from:string, removed:string[]}> }}
 */
export function dropUnsupportedOptionClaims(options, src) {
  const s = src || {};
  if (!Array.isArray(options) || !Array.isArray(s.options)) return { options, changed: [], empty: [] };
  const changed = [];
  const empty = [];
  const check = (name, own) => {
    const claim = dropUnsupportedClaims(name, { attributes: s.attributes, options: [own], productFeature: s.productFeature }, { keepPercent: true, allowEmpty: true });
    const ban = dropBannedFeatureWords(claim.name, s.productFeature, s.attributes, undefined, { allowEmpty: true });
    return { name: ban.name, removed: [...claim.removed, ...ban.removed] };
  };
  const out = options.map(o => {
    const own = o && s.options.find(x => x && x.optionNumber === o.optionNumber);
    if (!own || typeof o.korName !== 'string') return o;
    const first = check(o.korName, own);
    if (!first.removed.length) return o;
    let name = first.name;
    if (!name) {
      const trans = tidy(stripHan((own.option1 && own.option1.nameTrans) || '').text);
      name = trans ? check(trans, own).name : '';
    }
    if (!name) {
      empty.push({ optionNumber: o.optionNumber, from: o.korName, removed: first.removed });
      return o;
    }
    changed.push({ optionNumber: o.optionNumber, from: o.korName, to: name });
    return { ...o, korName: name };
  });
  return { options: out, changed, empty };
}

/**
 * 상품 특징의 금지 표현 — 견적서 속성 프롬프트(1688 속성을 그대로 주는 곳)에 넣을 한 줄(F84B02 «천연 앙고라·천연 밍크로 표현 금지»).
 * @param {string} productFeature
 * @returns {string} 금지 표현이 없으면 빈 글
 */
export function bannedWordsRule(productFeature) {
  const banned = bannedPhrasesFromFeature(productFeature);
  return banned.length ? `\n- 판매자 금지 표현(상품 특징): ${banned.join(', ')} — 1688 속성에 있어도 속성 값에 절대 쓰지 마세요` : '';
}

// 소재 칸 — 1688 속성 이름 · 쿠팡 속성 이름
const MATERIAL_ATTR_RE = /材质|材料|面料|재질|소재|원단|material/i;
const FEATURE_MATERIAL_RE = /(?:소재|재질)\s*(?:는|은|:)?\s*([가-힣A-Za-z]{2,10})/g;

/**
 * 원문 소재 — 1688 소재 칸 값(번역·원문) + 상품 특징 «소재는 X»(CB70ED «구슬 소재는 유리» · 재질=玻璃/유리).
 * @param {{ attributes?: Array, productFeature?: string }} src
 * @returns {string[]}
 */
export function sourceMaterials(src) {
  const s = src || {};
  const out = [];
  (Array.isArray(s.attributes) ? s.attributes : []).filter(a => a && MATERIAL_ATTR_RE.test(`${a.attributeName || ''} ${a.attributeNameTrans || ''}`))
    .forEach(a => [a.valueTrans, a.value].forEach(v => String(v || '').split(/[/,、·+]/).forEach(t => out.push(t.trim()))));
  const feat = featureWithoutBans(s.productFeature);
  let m;
  FEATURE_MATERIAL_RE.lastIndex = 0;
  while ((m = FEATURE_MATERIAL_RE.exec(feat))) out.push(m[1]);
  return [...new Set(out.filter(Boolean))];
}

function sameMaterial(a, b) {
  const x = String(a || '').toLowerCase().replace(/\s+/g, '');
  const y = String(b || '').toLowerCase().replace(/\s+/g, '');
  if (!x || !y) return false;
  if (x.includes(y) || y.includes(x)) return true;
  return MATERIALS.some(g => { const re = new RegExp(g.re.source, g.re.flags.replace('g', '')); return re.test(a) && re.test(b); });
}

/**
 * 만든 속성 값의 사실 검사 — 상품명과 같은 잣대(F84B02 · 4DBBFE):
 * ① 상품 특징의 금지 표현을 뺀다(dropBannedFeatureWords) ② 소재 칸은 원문 소재가 있을 때 근거 없는 소재 주장을 뺀다(dropUnsupportedClaims — 1688 재질=금속인데 «가죽»).
 * 다 빠지면 null(되살림 0) — 필수면 부른 쪽이 크게 멈춘다(emptyMandatoryAttributes · mandatoryUnexposedError).
 * @param {Array<{attributeName:string, attributeValue?:string, value?:string}>} attrs
 * @param {{ productFeature?: string, attributes?: Array, options?: Array }} src - 상품 특징 · 1688 속성 · 남긴 옵션
 * @returns {{ attributes: Array, removed: Array<{attributeName:string, from:string, to:string|null, removed:string[]}> }}
 */
export function dropUnsupportedAttributeValues(attrs, src) {
  const s = src || {};
  if (!Array.isArray(attrs)) return { attributes: attrs, removed: [] };
  const hasMaterial = sourceMaterials(s).length > 0;
  const removed = [];
  const out = attrs.map(a => {
    const key = a && typeof a.attributeValue === 'string' ? 'attributeValue' : (a && typeof a.value === 'string' ? 'value' : null);
    if (!key) return a;
    const ban = dropBannedFeatureWords(a[key], s.productFeature, s.attributes, undefined, { allowEmpty: true });
    let name = ban.removed.length ? ban.name : a[key];
    const words = [...ban.removed];
    if (name && hasMaterial && MATERIAL_ATTR_RE.test(String(a.attributeName || ''))) {
      const claim = dropUnsupportedClaims(name, { attributes: s.attributes, options: s.options || [], productFeature: s.productFeature }, { keepPercent: true, allowEmpty: true });
      if (claim.removed.length) { name = claim.name; words.push(...claim.removed); }
    }
    if (!words.length) return a;
    const to = name || null;
    removed.push({ attributeName: a.attributeName, from: a[key], to, removed: words });
    return { ...a, [key]: to };
  });
  return { attributes: out, removed };
}

/**
 * 필수(mandatory) 속성 중 값이 빈 것 — 응답에 없음 · null · 빈 글 · 한자 값(적용 때 null 이 된다)(CB70ED «주얼리 소재»).
 * @param {Array} attrs - 만든 속성
 * @param {Array<{name:string, mandatory:boolean}>} schemaAttrs - 스키마 가이드
 * @returns {string[]} 빈 필수 속성 이름
 */
export function emptyMandatoryAttributes(attrs, schemaAttrs) {
  const list = Array.isArray(attrs) ? attrs : [];
  return (Array.isArray(schemaAttrs) ? schemaAttrs : []).filter(s => s && s.mandatory).map(s => s.name).filter(name => {
    const a = list.find(x => x && x.attributeName === name);
    const v = a ? (a.attributeValue != null ? a.attributeValue : a.value) : null;
    return v == null || String(v).trim() === '' || hasHan(String(v));
  });
}

/**
 * 필수 비노출 속성을 못 만들었을 때 멈추는 오류(지어낸 값·기본값 0) — mandatoryUnexposed 표식이 있으면 받는 쪽이 삼키지 않는다.
 * 선택형(dropdown) 소재 칸인데 원문 소재가 선택지에 없으면 그 까닭을 적는다(CB70ED: 선택지 16개에 «유리» 없음).
 * @param {string[]} names - 빈 필수 속성
 * @param {{ schemaAttrs?: Array, src?: Object, cause?: string }} [ctx]
 */
export function mandatoryUnexposedError(names, ctx) {
  const c = ctx || {};
  const materials = sourceMaterials(c.src).filter(t => !hasHan(t));
  const lines = names.map(name => {
    const sa = (Array.isArray(c.schemaAttrs) ? c.schemaAttrs : []).find(x => x && x.name === name);
    const choices = sa && Array.isArray(sa.dropdown) ? sa.dropdown : [];
    if (choices.length && materials.length && MATERIAL_ATTR_RE.test(name) && !materials.some(t => choices.some(ch => sameMaterial(ch, t)))) {
      return `필수 비노출 속성 '${name}' 선택지에 원문 소재 '${materials.join('/')}'가 없어 등록을 멈춥니다 — 선택지: ${choices.join(', ')}`;
    }
    return `필수 비노출 속성 '${name}' 값을 만들지 못해 등록을 멈춥니다`;
  });
  const e = new Error(`${lines.join(' / ')}${c.cause ? ` (${c.cause})` : ''}`);
  e.mandatoryUnexposed = true;
  return e;
}

export default { fixOptionTerms, dropUnsupportedOptionClaims, bannedWordsRule, sourceMaterials, dropUnsupportedAttributeValues, emptyMandatoryAttributes, mandatoryUnexposedError, cnColorToKo, cnColorsKo,dropUnsupportedClaims, bannedPhrasesFromFeature, dropBannedFeatureWords, dropConflictingItemWords, hasHan, stripHan, dropItemCodes, dropSpecsNotInOptions, dropPartialOptionParens, summarizeOptionValues, splitProductName, isKidsCategory, stripKidsWords, dropAudienceNotInFeature, featureWithoutBans, dropUnsupportedTags, dropCountsNotInSource, markCapacityCounts, dropShapeWordsNotInOptions, dropLayerWordsNotInOptions };
